/**
 * Homepage marketing widget shell and artifact selection.
 *
 * The outer shell classes are intentionally duplicated in CMT for asset authoring preview — not shared via packages.
 * Keep visual parity with:
 * apps/admin/ui/src/app/pages/CampaignManagement/assets/components/SideCardPreviewTemplate.tsx
 * (template body markup lives in MarketingWidgetTemplate.tsx in this folder)
 * Changes to the widget shell must be reflected in the CMT preview, and vice versa.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { GetMeQuery } from '@/generated/gql/graphql';
import Skeleton from '@/enterprise/components/Skeleton';
import { useMarketingArtifacts, type Artifact } from '@/enterprise/data/useArtifacts';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useUserDetails from '@/shared/data/useUserDetails';
import MarketingWidgetTemplate from './MarketingWidgetTemplate';
import useClientMarketingWidget from './useClientMarketingWidget';
import { mapMarketingArtifactToWidgetProps } from './utils';

/** Canonical placement from CMT `SIDE_CARD` on Early Pay Home (enum segments → kebab-case). */
const HOMEPAGE_SIDECARD_PLACEMENT = 'early-pay.home.side-card';

type MarketingMessageArtifactContent = Extract<NonNullable<Artifact['content']>, { title: string }>;

const isMarketingMessageContent = (content: Artifact['content']): content is MarketingMessageArtifactContent =>
  content != null && 'title' in content;

function isRenderableMarketingArtifact(artifact: Artifact): boolean {
  const content = artifact.content;
  return (
    artifact.artifactType === 'MARKETING_MESSAGE' &&
    isMarketingMessageContent(content) &&
    content.title.length > 0 &&
    content.body.length > 0
  );
}

/** Returns the MARKETING_MESSAGE artifact with the highest priority; ties keep the first in array order. */
export function selectHighestPriorityMarketingArtifact(artifacts: Artifact[]): Artifact | undefined {
  const candidates = artifacts.filter(isRenderableMarketingArtifact);

  return candidates.reduce<Artifact | undefined>((best, current) => {
    if (!best || current.priority > best.priority) {
      return current;
    }

    return best;
  }, undefined);
}

const MarketingWidgetSkeleton = () => (
  <div className="space-y-4 px-6 py-10">
    <Skeleton className="h-20 w-1/2" />
    <Skeleton className="h-20 w-full" />
    <Skeleton className="h-10 w-1/2" />
  </div>
);

const ClientMarketingWidgetContent = () => {
  const widgetProps = useClientMarketingWidget();

  return <MarketingWidgetTemplate {...widgetProps} />;
};

const ClientMarketingWidget = () => (
  <QueryBoundaries LoadingComponent={MarketingWidgetSkeleton}>
    <ClientMarketingWidgetContent />
  </QueryBoundaries>
);

/**
 * Picks widget copy at runtime: campaign artifacts from SEA when available,
 * otherwise the client path in useClientMarketingWidget (feature flags, markets, buyer eligibility).
 *
 * Both paths render through `MarketingWidgetTemplate`, which accepts any `icon` ReactNode —
 * Liquidity components from `@c2fo/liquidity/icons` (client widgets and artifact icon keys).
 * Keep both mappers (`useClientMarketingWidget`, `mapMarketingArtifactToWidgetProps`) until client widgets
 * are fully replaced by campaigns.
 */
const MarketingWidgetContent = () => {
  const { addError } = useDataDog();
  const { data: accountId } = useUserDetails(useCallback((user: GetMeQuery) => user.me?.account?.id, []));
  const {
    data: artifacts,
    isLoading,
    isError,
    error,
  } = useMarketingArtifacts(accountId, (allArtifacts) =>
    allArtifacts.filter((artifact) => artifact.placements?.includes(HOMEPAGE_SIDECARD_PLACEMENT) ?? false)
  );

  const selectedArtifact = useMemo(() => {
    if (isError || !artifacts) {
      return undefined;
    }

    return selectHighestPriorityMarketingArtifact(artifacts);
  }, [artifacts, isError]);

  useEffect(() => {
    if (isError && error) {
      addError(error, { context: 'enterprise::marketing-widget::getArtifacts', accountId });
    }
  }, [isError, error, addError, accountId]);

  if (isLoading) {
    return <MarketingWidgetSkeleton />;
  }

  if (selectedArtifact) {
    return <MarketingWidgetTemplate {...mapMarketingArtifactToWidgetProps(selectedArtifact)} />;
  }

  return <ClientMarketingWidget />;
};

const MarketingWidget = () => (
  <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-blue-600 bg-gradient-to-b from-lightBlue-500/15 to-lightBlue-500/50 shadow-md">
    <MarketingWidgetContent />
  </div>
);

export default MarketingWidget;
